import { useCallback, useContext, useEffect, useMemo } from 'react';
import { Layer, Source } from 'react-map-gl';
import { Feature, Point } from 'geojson';
import { AreaSearchResult } from '@alltrails/search/types/algoliaResultTypes';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import { useGroupHover, useClientNavigationSearchParams, useClientNavigationPushState } from '@alltrails/core';
import { MapMouseEvent, type SymbolLayerSpecification, type FilterSpecification, type ExpressionSpecification } from 'mapbox-gl';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import { useExperiments } from '@alltrails/experiments';
import { useAppDispatch, useAppSelector } from '../../redux';
import { serializedCoordinatesToCoordinates } from '../../utils/serializedCoordinates';
import useSvgImages, { SvgDefinition } from '../../utils/useSvgImages';
import {
  defaultParkPinLayerLayoutProps,
  defaultSymbolLayerLayoutProps,
  getLayerBeforeId,
  nationalParkPinsId,
  parkPinsId,
  stateParkPinsId
} from '../../utils/layers';
import useMap from '../../hooks/useMap';
import { updateActivePark, updateCursor } from '../../redux/reducer';
import { MapLayerInteractionsContext } from '../MapLayerInteractionsProvider';
import { nationalParkPinSvg, activeNationalParkPinSvg } from '../../utils/svgDefinitions/nationalParkPinSvg';
import { defaultParkPinSvg, activeDefaultParkPinSvg } from '../../utils/svgDefinitions/defaultParkPinSvg';
import useHideMapboxParkLayers from '../../hooks/useHideMapboxParkLayers';
import ParkResultProperties from './ParkResultProperties';
import ParkPopup from './ParkPopup';

const svgDefinitions: SvgDefinition[] = [nationalParkPinSvg, activeNationalParkPinSvg, defaultParkPinSvg, activeDefaultParkPinSvg];

const sourceId = 'park-results-source';

export type ParkResultsProps = {
  onParkClick?: (park: AreaSearchResult) => void;
};

const ParkResults = ({ onParkClick }: ParkResultsProps) => {
  const map = useMap();
  const dispatch = useAppDispatch();
  const isMobile = useIsMobileSizedScreen();
  const languageRegionCode = useLanguageRegionCode();
  const searchParams = useClientNavigationSearchParams();
  const pushState = useClientNavigationPushState();
  const hasPreventClusteringExperiment = useExperiments()?.['web-discover-explore-clustering-rm']?.value === 'on';

  const { activePark, allowClickEvents, parkResults } = useAppSelector(state => ({
    activePark: state.map.activePark,
    allowClickEvents: state.map.allowClickEvents,
    parkResults: state.map.parkResults
  }));
  const getHref = useCallback(
    (park: AreaSearchResult) => {
      const slug = activePark?.slug || park?.slug;
      return wrapUrlSafe(`/explore/${slug}?${searchParams.toString()}`, languageRegionCode);
    },
    [activePark?.slug, languageRegionCode, searchParams]
  );

  const { registerEventHandler, unregisterEventHandler } = useContext(MapLayerInteractionsContext);

  const { imagesAreLoaded } = useSvgImages(svgDefinitions);

  const features = useMemo(() => {
    if (!parkResults) {
      return [];
    }
    return Object.keys(parkResults).reduce<Feature<Point, ParkResultProperties>[]>((allFeatures, serializedCoordinates) => {
      const park = parkResults[serializedCoordinates];
      if (!park || !park.area_type || !park.ID) {
        return allFeatures;
      }
      const { lat, lng } = serializedCoordinatesToCoordinates(serializedCoordinates);
      return [
        ...allFeatures,
        {
          geometry: { type: 'Point', coordinates: [lng, lat] },
          properties: { areaType: park.area_type, serializedCoordinates, parkId: park.ID },
          type: 'Feature'
        }
      ];
    }, []);
  }, [parkResults]);

  // Hide Mapbox default park pins when we have custom park pins rendered
  // Uses static expressions that check for AT_ID property (like iOS implementation)
  const hasParkPins = imagesAreLoaded && features.length > 0;
  useHideMapboxParkLayers(map, hasParkPins);

  const onClick = useCallback(
    (event: MapMouseEvent) => {
      if (event.features) {
        const marker = event.features[0] as unknown as Feature<Point, ParkResultProperties>;
        const park = parkResults?.[marker.properties.serializedCoordinates];
        if (park) {
          onParkClick?.(park);
          pushState(getHref(park));
        }
      }
    },
    [onParkClick, parkResults, pushState, getHref]
  );

  const onHover = useCallback(
    (properties?: ParkResultProperties) => {
      if (properties) {
        const serializedCoordinates = properties.serializedCoordinates;
        const park = parkResults?.[serializedCoordinates];
        if (park && park.ID !== activePark?.ID) {
          onParkClick?.(park);
          dispatch(updateActivePark(park));
        }

        dispatch(updateCursor('pointer'));
      }
    },
    [activePark?.ID, dispatch, onParkClick, parkResults]
  );

  const onHoverEnd = useCallback(() => {
    dispatch(updateActivePark(undefined));
    dispatch(updateCursor('unset'));
  }, [dispatch]);

  const { onMouseEnter, onMouseLeave } = useGroupHover(onHover, onHoverEnd, true, 50);

  const onMouseMove = useCallback(
    (event: MapMouseEvent) => {
      if (onMouseEnter && event.features) {
        const marker = event.features[0];
        onMouseEnter(marker.properties);
      }
    },
    [onMouseEnter]
  );

  useEffect(() => {
    if (!map) {
      return;
    }

    if (hasPreventClusteringExperiment) {
      // Register event handlers for both national and state park pin layers
      if (allowClickEvents) {
        registerEventHandler(nationalParkPinsId, 'click', onClick);
        registerEventHandler(stateParkPinsId, 'click', onClick);
      }
      if (!isMobile) {
        registerEventHandler(nationalParkPinsId, 'mousemove', onMouseMove);
        registerEventHandler(stateParkPinsId, 'mousemove', onMouseMove);
        if (onMouseLeave) {
          registerEventHandler(nationalParkPinsId, 'mouseleave', onMouseLeave);
          registerEventHandler(stateParkPinsId, 'mouseleave', onMouseLeave);
        }
      }
    } else {
      allowClickEvents && registerEventHandler(parkPinsId, 'click', onClick);
      !isMobile && registerEventHandler(parkPinsId, 'mousemove', onMouseMove);
      !isMobile && onMouseLeave && registerEventHandler(parkPinsId, 'mouseleave', onMouseLeave);
    }

    return () => {
      if (hasPreventClusteringExperiment) {
        unregisterEventHandler(nationalParkPinsId, 'click', onClick);
        unregisterEventHandler(stateParkPinsId, 'click', onClick);
        unregisterEventHandler(nationalParkPinsId, 'mousemove', onMouseMove);
        unregisterEventHandler(stateParkPinsId, 'mousemove', onMouseMove);
        if (onMouseLeave) {
          unregisterEventHandler(nationalParkPinsId, 'mouseleave', onMouseLeave);
          unregisterEventHandler(stateParkPinsId, 'mouseleave', onMouseLeave);
        }
      } else {
        unregisterEventHandler(parkPinsId, 'click', onClick);
        !isMobile && unregisterEventHandler(parkPinsId, 'mousemove', onMouseMove);
        onMouseLeave && !isMobile && unregisterEventHandler(parkPinsId, 'mouseleave', onMouseLeave);
      }
    };
  }, [
    isMobile,
    map,
    onMouseLeave,
    onMouseMove,
    registerEventHandler,
    unregisterEventHandler,
    onClick,
    allowClickEvents,
    hasPreventClusteringExperiment
  ]);

  if (!imagesAreLoaded || !features.length) {
    return null;
  }

  if (hasPreventClusteringExperiment) {
    const isNotNationalParkFilter: FilterSpecification = ['!=', ['get', 'areaType'], 'N'];
    const isNationalParkFilter: FilterSpecification = ['==', ['get', 'areaType'], 'N'];
    const isActiveParkExpression: ExpressionSpecification = ['==', ['get', 'parkId'], activePark?.ID ?? -1];

    const commonLayoutProps = {
      ...defaultSymbolLayerLayoutProps,
      ...defaultParkPinLayerLayoutProps,
      'icon-allow-overlap': true
    } as SymbolLayerSpecification['layout'];

    // National park layer layout
    const nationalParkLayerLayout = {
      ...commonLayoutProps,
      'icon-image': ['case', isActiveParkExpression, activeNationalParkPinSvg.name, nationalParkPinSvg.name]
    } as SymbolLayerSpecification['layout'];

    // State/regional park layer layout
    const stateParkLayerLayout = {
      ...commonLayoutProps,
      'icon-image': ['case', isActiveParkExpression, activeDefaultParkPinSvg.name, defaultParkPinSvg.name]
    } as SymbolLayerSpecification['layout'];

    return (
      <>
        <Source id={sourceId} type="geojson" data={{ type: 'FeatureCollection', features }}>
          {/* State/regional park pins layer - renders below national parks */}
          <Layer
            type="symbol"
            id={stateParkPinsId}
            source={sourceId}
            beforeId={getLayerBeforeId(map, stateParkPinsId)}
            filter={isNotNationalParkFilter}
            layout={stateParkLayerLayout}
          />
          {/* National park pins layer - renders above state parks */}
          <Layer
            type="symbol"
            id={nationalParkPinsId}
            source={sourceId}
            beforeId={getLayerBeforeId(map, nationalParkPinsId)}
            filter={isNationalParkFilter}
            layout={nationalParkLayerLayout}
          />
        </Source>
        {activePark && !isMobile && (
          <ParkPopup
            park={activePark}
            coordinates={[activePark._geoloc?.lng ?? 0, activePark._geoloc?.lat ?? 0]}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          />
        )}
      </>
    );
  } else {
    const isNationalParkExpression = ['==', ['get', 'areaType'], 'N'];
    const isActiveParkExpression = ['==', ['get', 'parkId'], activePark?.ID ?? -1];

    const parkLayerLayout = {
      ...defaultSymbolLayerLayoutProps,
      ...defaultParkPinLayerLayoutProps,
      'icon-image': [
        'case',
        isActiveParkExpression,
        ['case', isNationalParkExpression, activeNationalParkPinSvg.name, activeDefaultParkPinSvg.name],
        ['case', isNationalParkExpression, nationalParkPinSvg.name, defaultParkPinSvg.name]
      ]
    } as SymbolLayerSpecification['layout'];

    return (
      <>
        <Source id={sourceId} type="geojson" data={{ type: 'FeatureCollection', features }}>
          <Layer type="symbol" id={parkPinsId} source={sourceId} beforeId={getLayerBeforeId(map, parkPinsId)} layout={parkLayerLayout} />
        </Source>
        {activePark && !isMobile && (
          <ParkPopup
            park={activePark}
            coordinates={[activePark._geoloc?.lng ?? 0, activePark._geoloc?.lat ?? 0]}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          />
        )}
      </>
    );
  }
};

export default ParkResults;
